import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createTicket,
    getTickets,
    updateTicket,
    deleteTicket,
    createMemberTicket,
    getMemberTickets,
    getCenterMemberTickets,
    updateMemberTicket,
    refundMemberTicket,
    getMemberTicketBookableSessions,
    bulkReserveMemberTicketSessions,
} from './api';
import { groupTicketKeys } from './keys';
import { BulkReserveMemberTicketSessionsBody, CreateTicketBody, CreateMemberTicketBody, RefundMemberTicketBody } from './types';
import { invalidateSuperAdminDashboardQueries } from '@/lib/superAdminCache';
import { invalidateLegacyMemberQueries } from '@/features/publicMember/hooks';
import { groupSessionKeys } from '@/features/groupSession/keys';
import { groupBookingKeys } from '@/features/groupBooking/keys';

export const useTickets = ({ centerId, params, enabled = true }: { centerId: string; params?: Record<string, string>; enabled?: boolean }) =>
    useQuery({
        queryKey: [...groupTicketKeys.list(centerId), params],
        queryFn: () => getTickets(centerId, params),
        enabled: enabled && !!centerId,
        select: (data) => data?.data?.results ?? [],
    });

export const useMemberTickets = ({
    centerId,
    memberId,
    status,
    enabled = true,
    /**
     * 공유받은 그룹 수강권까지 포함할지. 서버는 opt-in일 때만 공유 행을 내려주고 각 행에
     * access를 붙인다. 켜는 화면은 공유 행에 소유자 액션(환불·삭제 등)을 노출하면 안 된다.
     */
    includeShared = false,
}: { centerId: string; memberId: string; status?: string; enabled?: boolean; includeShared?: boolean }) =>
    useQuery({
        queryKey: [...groupTicketKeys.memberList(centerId, memberId), status, includeShared],
        queryFn: () =>
            getMemberTickets(centerId, memberId, {
                ...(status ? { status } : {}),
                ...(includeShared ? { includeShared: 'true' } : {}),
            }),
        enabled: enabled && !!centerId && !!memberId,
        select: (data) => data?.data?.results ?? [],
    });

export const useCenterMemberTickets = ({ centerId, params, enabled = true }: { centerId: string; params?: Record<string, string>; enabled?: boolean }) =>
    useQuery({
        queryKey: [...groupTicketKeys.centerList(centerId), params],
        queryFn: () => getCenterMemberTickets(centerId, params),
        enabled: enabled && !!centerId,
        select: (data) => data?.data ?? null,
    });

export const useMemberTicketBookableSessions = ({
    centerId,
    memberTicketId,
    params,
    enabled = true,
}: {
    centerId: string;
    memberTicketId: string;
    params?: Record<string, string>;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...groupTicketKeys.bookableSessions(centerId, memberTicketId), params],
        queryFn: () => getMemberTicketBookableSessions(centerId, memberTicketId, params),
        enabled: enabled && !!centerId && !!memberTicketId,
        select: (data) => data?.data ?? null,
    });

export const useCreateTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: CreateTicketBody }) => createTicket(centerId, body),
        onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: groupTicketKeys.list(v.centerId) }); },
    });
};

export const useUpdateTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, ticketId, body }: { centerId: string; ticketId: string; body: Partial<CreateTicketBody> }) => updateTicket(centerId, ticketId, body),
        onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: groupTicketKeys.list(v.centerId) }); },
    });
};

export const useDeleteTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, ticketId }: { centerId: string; ticketId: string }) => deleteTicket(centerId, ticketId),
        onSuccess: (_, v) => { qc.invalidateQueries({ queryKey: groupTicketKeys.list(v.centerId) }); },
    });
};

export const useCreateMemberTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, memberId, body }: { centerId: string; memberId: string; body: CreateMemberTicketBody }) => createMemberTicket(centerId, memberId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupTicketKeys.memberList(v.centerId, v.memberId) });
            void invalidateLegacyMemberQueries({ queryClient: qc, centerId: v.centerId, memberId: v.memberId });
            void invalidateSuperAdminDashboardQueries(qc);
        },
    });
};

export const useUpdateMemberTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, memberTicketId, body }: { centerId: string; memberTicketId: string; memberId?: string; body: any }) => updateMemberTicket(centerId, memberTicketId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: ['membershipChangeLog'] });
            void invalidateLegacyMemberQueries({ queryClient: qc, centerId: v.centerId, memberId: v.memberId });
        },
    });
};

export const useRefundMemberTicket = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ centerId, memberTicketId, body }: { centerId: string; memberTicketId: string; memberId?: string; body: RefundMemberTicketBody }) => refundMemberTicket(centerId, memberTicketId, body),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: ['membershipChangeLog'] });
            void invalidateLegacyMemberQueries({ queryClient: qc, centerId: v.centerId, memberId: v.memberId });
            void invalidateSuperAdminDashboardQueries(qc);
        },
    });
};

export const useBulkReserveMemberTicketSessions = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            centerId,
            memberTicketId,
            memberId,
            body,
        }: {
            centerId: string;
            memberTicketId: string;
            memberId?: string;
            body: BulkReserveMemberTicketSessionsBody;
        }) =>
            // 공유 수강권은 소유자가 아닌 참여자를 예약할 수 있어야 한다. memberId를 안 보내면
            // 서버가 소유자를 예약해 엉뚱한 회원에게 수업이 잡힌다.
            bulkReserveMemberTicketSessions(centerId, memberTicketId, {
                ...body,
                ...(memberId ? { memberId } : {}),
            }),
        onSuccess: (_, v) => {
            qc.invalidateQueries({ queryKey: groupTicketKeys.bookableSessions(v.centerId, v.memberTicketId) });
            qc.invalidateQueries({ queryKey: groupTicketKeys.all });
            qc.invalidateQueries({ queryKey: groupSessionKeys.all });
            qc.invalidateQueries({ queryKey: groupBookingKeys.all });
            qc.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
            qc.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
            void invalidateLegacyMemberQueries({ queryClient: qc, centerId: v.centerId, memberId: v.memberId });
        },
    });
};
